import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, TrendingUp, X } from 'lucide-react';
import type { PromotionAcceptResult, PromotionCandidate } from '../../generated/api/types.gen.js';
import {
  acceptPromotionCandidateMutation,
  listPromotionCandidatesOptions,
  listPromotionCandidatesQueryKey,
  rejectPromotionCandidateMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Callout, DataTable, EmptyState, Modal, useToast } from '../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../components/ui/index.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';

interface PromotionCandidatesListProps {
  /** Include candidates that were already decided. */
  showAll: boolean;
  /** Draw the table's own card frame (when it is not inside a list page's card). */
  framed?: boolean;
}

const STATUS: Record<PromotionCandidate['status'], { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Pending review', variant: 'neutral' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
  deferred: { label: 'Deferred', variant: 'warning' },
};

function downloadPatch(candidateId: number, patch: string) {
  const url = URL.createObjectURL(new Blob([patch], { type: 'text/x-patch' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `promote-${candidateId}.patch`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Tier-3 to tier-2 event-type promotion candidates, with generate-patch and reject for pending ones. */
export const PromotionCandidatesList: React.FC<PromotionCandidatesListProps> = ({ showAll, framed }) => {
  const help = PAGE_HELP['promotion-candidates'];
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [latest, setLatest] = useState<{ candidateId: number; result: PromotionAcceptResult } | null>(null);

  const query = useQuery(listPromotionCandidatesOptions({ query: { status: showAll ? 'all' : 'pending' } }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: listPromotionCandidatesQueryKey() });

  const accept = useMutation({
    ...acceptPromotionCandidateMutation(),
    onSuccess: async (result, variables) => {
      setLatest({ candidateId: Number(variables.path.id), result });
      await refresh();
    },
    onError: () => notify('Could not generate the patch', 'danger'),
  });
  const reject = useMutation({
    ...rejectPromotionCandidateMutation(),
    onSuccess: async () => {
      notify('Candidate rejected');
      await refresh();
    },
    onError: () => notify('Could not reject the candidate', 'danger'),
  });

  const columns: DataTableColumn<PromotionCandidate>[] = [
    {
      key: 'promotion',
      header: 'Promotion',
      render: (c) => (
        <div className="min-w-0">
          <span className="block text-sm font-medium text-heading">
            {c.eventTypes.join(', ')} to the {c.recommendedModule ?? 'unknown'} module
          </span>
          <span className="block text-xs text-muted">Template {c.templateId ?? 'unknown'}</span>
        </div>
      ),
    },
    {
      key: 'usage',
      header: 'Used in',
      render: (c) => (
        <span className="text-sm text-body">
          {c.projectCount} {c.projectCount === 1 ? 'project' : 'projects'}, {c.promotionCount}{' '}
          {c.promotionCount === 1 ? 'promotion' : 'promotions'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => (
        <div>
          <Badge variant={STATUS[c.status].variant} dot>
            {STATUS[c.status].label}
          </Badge>
          {c.reviewedBy && (
            <span className="mt-1 block text-xs text-muted">
              Reviewed by user {c.reviewedBy}
              {c.reviewedAt && ` on ${new Date(c.reviewedAt).toLocaleDateString()}`}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '260px',
      render: (c) =>
        c.status === 'pending' ? (
          <Can policy="RequireSuperadmin">
            <div className="flex items-center justify-end gap-1.5">
              <Button
                size="sm"
                loading={accept.isPending && accept.variables?.path.id === String(c.id)}
                onClick={() => accept.mutate({ path: { id: String(c.id) } })}
              >
                <CheckCircle2 size={14} aria-hidden="true" />
                Generate patch
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={reject.isPending && reject.variables?.path.id === String(c.id)}
                onClick={() => reject.mutate({ path: { id: String(c.id) } })}
              >
                <X size={14} aria-hidden="true" />
                Reject
              </Button>
            </div>
          </Can>
        ) : null,
    },
  ];

  return (
    <>
      <Modal
        open={latest !== null}
        onClose={() => setLatest(null)}
        title={latest ? `Patch ready for ${latest.result.patch.filePath}` : 'Patch ready'}
        maxWidth="2xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setLatest(null)}>
              Close
            </Button>
            <Button
              disabled={!latest?.result.patch.patch}
              onClick={() => latest && downloadPatch(latest.candidateId, latest.result.patch.patch)}
            >
              <Download size={14} aria-hidden="true" />
              Download patch
            </Button>
          </>
        }
      >
        {latest && (
          <>
            <p className="mb-2 text-sm text-muted">
              {latest.result.patch.fileExists ? 'Changes an existing file' : 'Creates a new file'} and adds{' '}
              {latest.result.patch.addedEventTypes.join(', ')}.
            </p>
            <pre className="max-h-64 overflow-auto whitespace-pre rounded-sm bg-surface-alt p-3 text-xs">
              {latest.result.patch.patch || 'No changes. The file is already up to date.'}
            </pre>
            <p className="mt-3 text-xs text-muted">
              Apply it with <code className="bg-surface-alt px-1">git apply promote-{latest.candidateId}.patch</code> in
              your checkout, then commit and open a pull request.
            </p>
          </>
        )}
      </Modal>
      {query.isError ? (
        <Callout
          tone="danger"
          title="Could not load promotion candidates"
          actions={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Callout>
      ) : (
        <DataTable
          framed={framed}
          columns={columns}
          rows={query.data ?? []}
          rowKey={(c) => c.id}
          loading={query.isPending}
          aria-label="Promotion candidates"
          empty={
            <EmptyState
              icon={TrendingUp}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
            />
          }
        />
      )}
    </>
  );
};
